import { AuthGuard, AuthProvider, createLocalStorageAdapter, createOAuth2PKCEProvider, useAuth } from "@zero/auth";
import { resolveApiBase } from "@zero/utils/api-base";
import { CheckCircle2, LogOut } from "lucide-react";
import { motion } from "motion/react";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import { KanbanBoard } from "./components/kanban";
import { ThemeToggle } from "./components/ThemeToggle";
import { TodoFilters } from "./components/TodoFilters";
import { TodoForm } from "./components/TodoForm";
import { TodoList } from "./components/TodoList";
import { ViewToggle } from "./components/ViewToggle";
import { API_CONFIG } from "./config/api";
import { useTodoStore } from "./store/todoStore";

const TodoDetailPage = lazy(() =>
  import("./components/TodoDetailPage").then((module) => ({ default: module.TodoDetailPage })),
);

const IDAAS_URL = resolveApiBase(import.meta.env.VITE_IDAAS_BASE_URL, "http://localhost:50051");
const OAUTH2_CLIENT_ID = import.meta.env.VITE_OAUTH2_CLIENT_ID;
if (!OAUTH2_CLIENT_ID?.trim()) {
  throw new Error("VITE_OAUTH2_CLIENT_ID is required");
}
const OAUTH2_RESOURCE = import.meta.env.VITE_IDAAS_RESOURCE_AUDIENCE;
if (!OAUTH2_RESOURCE?.trim()) {
  throw new Error("VITE_IDAAS_RESOURCE_AUDIENCE is required");
}
const OAUTH2_REDIRECT_URI = import.meta.env.VITE_OAUTH2_REDIRECT_URI || `${window.location.origin}/`;

const authStrategy = createOAuth2PKCEProvider({
  kind: "oauth2-pkce",
  idaasBaseUrl: IDAAS_URL,
  clientId: OAUTH2_CLIENT_ID,
  resource: OAUTH2_RESOURCE,
  // 浏览器端令牌校验期望的 iss：部署面经 /.zero/runtime.json 运行时下发
  // （99 D31，不进 bundle）；此读取仅保留 vite dev 覆盖，未设置时由
  // @zero/auth 内置的运行时通道解析，最终回落页面 origin。
  issuer: import.meta.env.VITE_OAUTH2_ISSUER,
  redirectUri: OAUTH2_REDIRECT_URI,
});

const authConfig = {
  storage: createLocalStorageAdapter(),
};

type AppRoute = { name: "home" } | { name: "detail"; todoId: string };

function readRoute(): AppRoute {
  const match = window.location.pathname.match(/^\/todo\/([^/]+)$/);
  if (match?.[1]) {
    return { name: "detail", todoId: decodeURIComponent(match[1]) };
  }
  return { name: "home" };
}

function AppContent() {
  const { activeCount, completedCount, todos, setFormExpanded, fetchTodos, isLoading, viewMode } = useTodoStore();
  const { getAuthHeaders, logout, user } = useAuth();
  const [route, setRoute] = useState<AppRoute>(() => readRoute());
  const active = activeCount();
  const completed = completedCount();

  const navigate = useCallback((path: string) => {
    window.history.pushState({}, "", path);
    setRoute(readRoute());
  }, []);

  const handleOpenTodo = useCallback((id: string) => navigate(`/todo/${encodeURIComponent(id)}`), [navigate]);
  const handleBackToList = useCallback(() => navigate("/todo"), [navigate]);

  useEffect(() => {
    API_CONFIG.setAuthContext({
      getAuthHeaders,
      getUserId: () => {
        if (!user?.sub) {
          throw new Error("User not authenticated");
        }
        return user.sub;
      },
    });
    return () => API_CONFIG.clearAuthContext();
  }, [getAuthHeaders, user?.sub]);

  // 加载 Todo 数据
  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  useEffect(() => {
    const handlePopState = () => setRoute(readRoute());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Welcome effect on first load
  useEffect(() => {
    if (todos.length === 0) {
      setFormExpanded(true);
    }
  }, [todos.length, setFormExpanded]);

  const total = todos.length;
  const isCompleted = total > 0 && completed === total;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background transition-colors">
      <Toaster position="top-center" theme="system" />

      {/* Header */}
      <header className="bg-card shadow-sm border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div
              className={`w-10 h-10 rounded-md flex items-center justify-center ${
                isCompleted ? "bg-success" : active > 0 ? "bg-primary" : "bg-muted"
              }`}
            >
              <CheckCircle2 size={20} className="text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Todo App</h1>
              <p className="text-sm text-muted-foreground">
                {total === 0 ? "开始创建你的任务" : isCompleted ? "所有任务已完成！" : `${active} 个任务待完成`}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="text-sm text-muted-foreground">{user?.email ?? user?.sub}</div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title="登出"
            >
              <LogOut size={18} />
            </button>
            <ThemeToggle />
          </motion.div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Suspense
            fallback={
              <output className="h-48 rounded-md border border-border bg-card animate-pulse" aria-label="加载中" />
            }
          >
            {route.name === "detail" ? (
              <TodoDetailPage todoId={route.todoId} onBack={handleBackToList} />
            ) : (
              <>
                <TodoForm />
                <TodoFilters />

                {/* 视图切换 */}
                <div className="flex justify-end mb-4">
                  <ViewToggle />
                </div>

                {/* 条件渲染 */}
                {viewMode === "list" ? (
                  <TodoList onOpenTodo={handleOpenTodo} disabled={isLoading} />
                ) : (
                  <KanbanBoard onOpenTodo={handleOpenTodo} disabled={isLoading} />
                )}
              </>
            )}
          </Suspense>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center text-sm text-muted-foreground">
        <p>zero Admin Monorepo - Todo App Demo</p>
      </footer>
    </div>
  );
}

/** OAuth2 回调参数是否出现在当前 URL（redirect_uri 即站点根路径）。 */
function hasOAuthCallbackParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("code") || params.has("error");
}

/**
 * 根路径回调壳：redirect_uri 是站点根（`/?code=...`），必须先完成 PKCE 码
 * 交换再挂 AuthGuard——Guard 的自动 startLogin 会在交换完成前再次发起
 * authorize，与回调参数形成登录循环。
 */
function TodoAuthShell() {
  const { handleCallback } = useAuth();
  const [callbackState, setCallbackState] = useState<"idle" | "pending" | "failed">(() =>
    hasOAuthCallbackParams() ? "pending" : "idle",
  );
  const [failure, setFailure] = useState("");
  const called = useRef(false);

  useEffect(() => {
    if (callbackState !== "pending" || called.current) return;
    called.current = true;
    void handleCallback()
      .then(() => {
        window.history.replaceState(null, "", "/");
        setCallbackState("idle");
      })
      .catch((error: unknown) => {
        setFailure(error instanceof Error ? error.message : String(error));
        setCallbackState("failed");
      });
    // 仅在挂载时消费一次回调参数；URL 变化不重触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (callbackState === "pending") {
    return <div className="grid min-h-screen place-items-center p-6 text-sm text-muted-foreground">正在完成登录…</div>;
  }
  if (callbackState === "failed") {
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center">
        <div>
          <p className="text-sm text-destructive">登录未能完成：{failure}</p>
          <button
            type="button"
            className="mt-4 rounded-md border px-4 py-2 text-sm"
            onClick={() => {
              window.history.replaceState(null, "", "/");
              setCallbackState("idle");
            }}
          >
            重新登录
          </button>
        </div>
      </div>
    );
  }
  return (
    <AuthGuard>
      <AppContent />
    </AuthGuard>
  );
}

function App() {
  return (
    <AuthProvider provider={authStrategy} config={authConfig}>
      <TodoAuthShell />
    </AuthProvider>
  );
}

export default App;
